import { useEffect, useState, type RefObject } from 'react';

/**
 * Returns true when the observed container's width is below `breakpointPx`.
 *
 * WHY: Tailwind `md:hidden` uses viewport width, which fails in narrow
 * embedded containers (e.g. desktop preview iframes). This hook uses
 * ResizeObserver on the actual layout container, making the bottom nav
 * visibility independent of the global viewport size.
 */
export function useContainerBreakpoint(
  ref: RefObject<HTMLElement | null>,
  breakpointPx = 768,
): boolean {
  const [isNarrow, setIsNarrow] = useState(true); // default narrow (mobile-first)

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      if (entry) {
        setIsNarrow(entry.contentRect.width < breakpointPx);
      }
    });

    // Initial measure
    setIsNarrow(el.getBoundingClientRect().width < breakpointPx);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, breakpointPx]);

  return isNarrow;
}
